import React, { useEffect, useState } from 'react';

import { Button, Group, LoadingOverlay, Modal, Select, SelectItem, Stack, Text, Title } from '@mantine/core';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import useAthletePackages from 'hooks/athletePackage/useAthletePackages';
import useCreateIssuedDocument from 'hooks/document/useCreateIssuedDocument';
import { AthletePackageList, AthleteRetrieve } from 'types';
import { formatAthletePackageValidityDisplay } from 'utils/athlete-package.helpers';
import notificationHandler from 'utils/notifications';

type IAthletePackageSelectItem = AthletePackageList & SelectItem;

const ContractCreateModal = ({
  opened,
  athlete,
  closeHandler,
}: {
  opened: boolean;
  athlete: AthleteRetrieve;
  closeHandler: () => void;
}): JSX.Element => {
  const { dateFormat } = useLocalization();
  const { successNotification, errorNotification } = notificationHandler();
  const { session } = useAuth();

  const createMutation = useCreateIssuedDocument();
  const [athletePackage, setAthletePackage] = useState<IAthletePackageSelectItem | null>(null);
  const [athletePackages, setAthletePackages] = useState<IAthletePackageSelectItem[]>([]);
  const { data, isLoading, isSuccess } = useAthletePackages({
    limit: '100',
    athlete: athlete.athleteId,
    statuses: ['ACTIVE', 'UPCOMING'],
  });

  const handleChange = (value: string): void => {
    const selected = athletePackages.filter(ap => ap.value === value)[0];
    setAthletePackage(selected);
  };

  const handleCloseAndReset = (): void => {
    setAthletePackage(null);
    closeHandler();
  };

  const handleSubmit = (): void => {
    if (athletePackage && athletePackage.package.contract) {
      createMutation.mutate(
        {
          athlete: athlete.athleteId,
          athletePackage: athletePackage.athletePackageId,
          template: athletePackage.package.contract,
        },
        {
          onError: error => {
            errorNotification('Contract Creation Failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Contract Created', 'Contract has been created successfully');
            handleCloseAndReset();
          },
          onSettled: () => {
            handleCloseAndReset();
          },
        },
      );
    }
  };

  useEffect(() => {
    if (isSuccess && data?.pages) {
      setAthletePackages(
        data.pages
          .map(page => page.data.results)
          .flat()
          .map(ap => ({
            ...ap,
            value: ap.athletePackageId,
            label: `${ap.package.name} ${formatAthletePackageValidityDisplay(
              ap,
              dateFormat,
              ' - ',
              session?.gym?.timezone,
            )}`,
          }))
          .filter(ap => ap.package.contract),
      );
    }
  }, [data?.pages, dateFormat, isSuccess, session]);

  return (
    <Modal
      opened={opened}
      onClose={handleCloseAndReset}
      size='auto'
      centered
      title={<Title order={4}>Create Contract</Title>}
    >
      <LoadingOverlay visible={isLoading} />
      <Stack spacing='md'>
        <Text color='dimmed' size='sm'>
          Select from upcoming or active packages linked to this user.
        </Text>
        <Text color='dimmed' size='sm'>
          This will generate a contract from the template linked to the package.
        </Text>
        <Select
          label='Select a Package'
          required
          data={athletePackages}
          placeholder='Select package'
          value={athletePackage?.value || ''}
          onChange={handleChange}
          nothingFound='No valid packages were found'
        />
        <Group position='right'>
          <Button variant='light' onClick={handleCloseAndReset} size='xs' radius='md'>
            Cancel
          </Button>
          <Button
            type='submit'
            onClick={handleSubmit}
            size='xs'
            radius='md'
            disabled={isLoading || createMutation.isLoading}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ContractCreateModal;
